<template>
    <div class="main">
        <div class="inputs">
            <input id="newProjectName" class="inputStyle" placeholder="Project Name" type="text" v-model="projectName"/>
            <input id="newProjectDescription" class="inputStyle" placeholder="Project Description" type="text" v-model="projectDescription"/>

            <input id="newProjectImage" class="inputStyle" type="file" accept="image/*" @change="handleProjectImage" />
            <div v-if="isEditMode && projectImageUrl" class="preview-container">
                <img :src="projectImageUrl" class="preview-image" alt="Project Preview">
            </div>

            <button class="user-button" @click="showUserSelector = !showUserSelector">
                {{ showUserSelector ? 'Hide' : 'Manage' }} Users
            </button>

            <div v-if="showUserSelector" class="user-list">
                <div v-for="user in users" :key="user.user_id" class="user-entry">
                    <input type="checkbox" :id="'user-' + user.user_id" :checked="isUserSelected(user.user_id)" @change="toggleUser(user.user_id)" />
                    <label :for="'user-' + user.user_id">{{ user.username }} ({{ user.email }})</label>
                </div>
            </div>            
        </div>
        <div ref="dropZone" class="dropZone" :class="{ 'highlight': isDragging }" @click="openFileDialog">
            <img class="dropImg" src="../assets/box-opened.png" alt="">
            <label ref="lblDropImages" class="dropLabel" :class="{'droplabel-upload':uploading}">Drop Images or Click</label>
            <p v-if="uploading">
                {{ uploadProgress.current }} / {{ uploadProgress.total }} Sent Images
            </p>

            <input ref="fileInput" type="file" accept=".png, .jpg, .jpeg, .gif, .bmp" multiple style="display: none;" @change="handleFileSelect" />
        </div>
        <div class="buttons">
            <Button :class="{'cancelVisible':isCancelVisible, 'cancelNotVisible':!isCancelVisible}" color="#d1d1d1" @click="isEditMode ? saveChanges() : CreateProject()">{{ isEditMode ? 'SAVE' : 'CREATE' }}</Button>
            <Button :class="{'cancelVisible':isCancelVisible, 'cancelNotVisible':!isCancelVisible}" color="red" @click="Cancel">CANCEL</Button>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, ref, onMounted, onUnmounted  } from "vue"
    import Button from "../components/Button.vue"
    import ProjectService from "../services/Project";
    import { Project } from "../helpers/Interfaces";
    
    export default defineComponent ({
        components: {
            Button
        },
        props: {
        initialProject: {
            type: Object as () => Project | null,
            default: null
        },
        isEditMode: {
            type: Boolean,
            default: false
        }
        },        
        setup(props, { emit }){
            const dropZone = ref<HTMLElement | null>(null);
            const fileInput = ref<HTMLInputElement | null>(null);
            const lblDropImages = ref<HTMLLabelElement | null>(null);
            const isDragging = ref(false);
            const projectName = ref<string>("");
            const projectDescription = ref<string>("");
            const projectImages = ref<File[]>([]);
            const existingImagesCount = ref<number>(0);
            const uploading = ref<boolean>(false);
            const uploadProgress = ref({ current: 0, total: 0 });
            const isCancelVisible = ref<boolean>(true);
            const projectImage = ref<File | null>(null);
            const projectImageUrl = ref<string>("");

            const showUserSelector = ref(false);
            const users = ref<any[]>([]);
            const selectedUsers = ref<string[]>([]);

            const openFileDialog = () => {
                if (fileInput.value) {
                    fileInput.value.click();
                }
            };

            const handleFileSelect = (event: Event) => {
                const input = event.target as HTMLInputElement;
                const files = input.files;

                if (files) {
                    processFiles(files);
                }
            };

            function handleDrop(event: DragEvent) {
                event.preventDefault();
                const files = event.dataTransfer?.files;

                if (files) {
                    processFiles(files);
                }

                isDragging.value = false;
            }

            const handleMainImage = (event: Event) => {
                const input = event.target as HTMLInputElement;
                const file = input.files?.[0];
                if (file) {
                    projectImage.value = file;
                }
            };

            const processFiles = (files: FileList) => {
                const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp'];
                const newFiles = Array.from(files).filter((f) => allowedExtensions.includes(f.name.slice(f.name.lastIndexOf('.')).toLowerCase()));
                projectImages.value.push(...newFiles);

                const totalDisplayed = existingImagesCount.value + projectImages.value.length;
                lblDropImages.value!.innerText = `Images: ${totalDisplayed}`;

                // projectImages.value.push(...Array.from(files).filter((file) => {
                //     const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
                //     return allowedExtensions.includes(fileExtension);
                // }));

                // if (projectImages.value.length > 0) {
                //     if (lblDropImages.value) {
                //         lblDropImages.value.innerText = "Images: " + projectImages.value.length;
                //     }
                // } else {
                //     alert('Only image files with the following extensions are allowed: .png, .jpg, .jpeg, .gif, .bmp');
                // }
            };

            function handleDragOver(event: DragEvent) {
                isDragging.value = true;
                event.preventDefault();
            }

            function handleDragLeave(event: DragEvent) {
                isDragging.value = false;
                event.preventDefault();
            }

            function Cancel() {
                projectName.value = "";
                projectDescription.value = "";
                projectImages.value = [];
                emit('cancelNewProject');
            }

            const isUserSelected = (userId: string) => selectedUsers.value.includes(userId);

            const toggleUser = (userId: string) => {
                if (selectedUsers.value.includes(userId)) {
                    selectedUsers.value = selectedUsers.value.filter(id => id !== userId);
                } else {
                    selectedUsers.value.push(userId);
                }
            };

            async function CreateProject()
            {
                try {
                    if (projectName.value.trim() == "" || projectName.value == null) {
                            alert("Project name cannot be empty.");
                            return;
                    }

                    // Check if project exists
                    const existingProject = await ProjectService.getProjectByName(projectName.value);
                    if (existingProject) {
                        alert("A project with the same name already exists.");
                        return;
                    }
                    
                    // Create a new project object
                    const newProject: Project = {
                        project_name: projectName.value,
                        project_description: projectDescription.value,
                        nr_images: projectImages.value.length,
                        project_image: projectImage?.value?.name ?? ""
                    }
                    
                    // Insert in projects table
                    const createdProject = await ProjectService.createProject(newProject);

                    if (projectImage.value) {
                        await ProjectService.uploadProjectImage(projectImage.value);
                    }
                    
                    await ProjectService.setProjectUsers(createdProject.id!, selectedUsers.value);

                    if (projectImages.value.length > 0) {
                        if (lblDropImages.value) lblDropImages.value.innerText = "Uploading...";
                        uploading.value = true;

                        // Send images to supabase storage and returns metadata
                        const imagesMetadata = await ProjectService.uploadImagesToSupabaseStorage(
                            createdProject.id!,
                            projectImages.value,
                            (current, total) => { uploadProgress.value = { current, total }; }
                        );

                        // Insert metadata
                        await ProjectService.insertProjectImagesMetadata(
                            createdProject.id!,
                            imagesMetadata
                        );

                        // Updates image count
                        await ProjectService.updateProjectImageCount(
                            createdProject.id!,
                            imagesMetadata.length
                        );

                        uploading.value = false;
                    }

                    // Clears form fields
                    projectName.value = "";
                    projectDescription.value = "";
                    projectImages.value = [];
                    emit("cancelNewProject");                
                } catch (err) {
                    console.error("Error creating project:", err);
                    alert("Error creating project, check console.");
                } finally {
                    // Reset do label
                    uploading.value = false;
                    if (lblDropImages.value) {
                        lblDropImages.value.innerText = "Drop Images or Click";
                    }
                }
            }

            async function saveChanges() {
                if (!props.initialProject) return;

                try {
                    await ProjectService.updateProject(props.initialProject.id!, {
                        project_name: projectName.value,
                        project_description: projectDescription.value,
                        project_image: projectImage.value?.name
                    });

                    if (projectImage.value) {
                        await ProjectService.uploadProjectImage(projectImage.value);
                    }

                    await ProjectService.setProjectUsers(props.initialProject.id!, selectedUsers.value);

                    if (projectImages.value.length) {
                        lblDropImages.value!.innerText = 'Uploading...';
                        uploading.value = true;
                        
                        const existing = await ProjectService.getImagesFromProject(props.initialProject.id!);
                        const existingNames = existing.map((img) => img.file_name);
                        const newImages = projectImages.value.filter((f) => !existingNames.includes(f.name));

                        if (newImages.length) {
                            const metadata = await ProjectService.uploadImagesToSupabaseStorage(
                            props.initialProject.id!,
                            newImages,
                            (c, t) => (uploadProgress.value = { current: c, total: t })
                            );
                            await ProjectService.insertProjectImagesMetadata(props.initialProject.id!, metadata);

                            const newTotal = existing.length + metadata.length;
                            await ProjectService.updateProjectImageCount(props.initialProject.id!, newTotal);
                            existingImagesCount.value = newTotal;
                        }
                        uploading.value = false;
                    }

                    emit('cancelNewProject');
                } catch (err) {
                    console.error("Error updating project:", err);
                    alert("Error updating project.");
                }
            }

            onMounted(async() => {
                if (props.initialProject) {
                    projectName.value = props.initialProject.project_name;
                    projectDescription.value = props.initialProject.project_description ?? '';
                    if (props.initialProject.id) {
                        projectImageUrl.value = await ProjectService.getProjectImageUrl(props.initialProject.id);
                        selectedUsers.value = await ProjectService.getUserIdsForProject(props.initialProject.id);
                    }                    
                }

                if (props.isEditMode && props.initialProject && props.initialProject.id) {
                    const imageCount = await ProjectService.getProjectImageCount(props.initialProject.id);
                    if (lblDropImages.value) {
                        lblDropImages.value.innerText = "Images: " + imageCount;
                    }
                }

                users.value = await ProjectService.getAllUsers();

                if (dropZone.value) {
                    // Attach event listeners once the component is mounted
                    dropZone.value.addEventListener('dragover', handleDragOver);
                    dropZone.value.addEventListener('dragleave', handleDragLeave);
                    dropZone.value.addEventListener('drop', handleDrop);
                }
            });

            onUnmounted(() => {
                if (dropZone.value) {
                    // Clean up event listeners when the component is unmounted
                    dropZone.value.removeEventListener('dragover', handleDragOver);
                    dropZone.value.removeEventListener('dragleave', handleDragLeave);
                    dropZone.value.removeEventListener('drop', handleDrop);
                }
            });

            return { Cancel, CreateProject, dropZone, lblDropImages, isDragging, fileInput, openFileDialog, handleFileSelect,
                     projectName, projectDescription, uploading, isCancelVisible, uploadProgress, handleProjectImage: handleMainImage, saveChanges,
                     projectImageUrl, showUserSelector, users, isUserSelected, toggleUser };
        }
    });
</script>

<style lang="scss" scoped>
    .main {
        display: flex;
        flex-direction: column;
    }

    .inputs {
        display: flex;
        flex-direction: column;
    }

    /* Autofill styles for Chrome and Safari */
    input:-webkit-autofill {
        -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
        -webkit-text-fill-color: #c1c1c1 !important;
        border-color: rgb(49, 49, 49) !important;
        transition: background-color 5000s ease-in-out 0s; /* Trick to keep background-color */
    }

    /* ::after pseudo-element to mask the autofill */
    input:-webkit-autofill::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: transparent; /* This will visually mask the autofill */
    }

    /* For Firefox */
    input:autofill {
        background-color: transparent !important;
        color: #c1c1c1 !important;
    }

    .inputStyle {
        padding: 10px;
        margin-top: 10px;
        background: transparent;
        border: 2px solid rgb(49, 49, 49);
        border-radius: 4px;
        color: #c1c1c1;
        width: 350px;
    }

    .dropZone {
        margin-top: 20px;
        width: 370px;
        height: 200px;
        border: 2px dashed #5c5c5c;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        font-size: 24px;
        color: #666;
    }

    .dropImg {
        opacity: 50%;
    }

    .dropLabel {
        margin-top: 20px;
    }

    @keyframes bounce {
        0%, 20%, 50%, 80%, to {
            transform: translateY(5px)
        }

        40% {
            transform: translateY(-25px)
        }

        60% {
            transform: translateY(-25px)
        }
    };

    @keyframes colorBounce {
        0%, 20%, 50%, 80%, 100% {
            color: #3498db; /* Start and End color */
        }

        40% {
            color: #e74c3c; /* Middle color */
        }

        60% {
            color: #f1c40f; /* Another intermediate color */
        }
    }

    .droplabel-upload {
        -webkit-animation: bounce 3s cubic-bezier(.37,0,.21,1.02) infinite, colorBounce 5s linear infinite;
        animation: bounce 3s cubic-bezier(.37,0,.21,1.02) infinite, colorBounce 5s linear infinite;
    }

    .highlight {
        background-color: #747474;
        border-color: #ffffff;
        color: #d4d4d4;
    }

    .buttons {
        display: flex;
        flex-direction: row;
        justify-content: space-evenly;
        align-items: center;
        margin-top: 20px;
        width: 370px;
    }

    .cancelVisible {
        opacity: 100%;
        pointer-events: all;
    }

    .cancelNotVisible {
        opacity: 30%;
        pointer-events: none;
    }

    .preview-image {
        margin-top: 20px;
        max-width: 350px;
        height: auto;
        border: 1px solid #444;
        border-radius: 4px;
    }

    .user-button {
        margin-top: 10px;
        padding: 5px 10px;
        background: #444;
        border: 1px solid #666;
        border-radius: 4px;
        color: white;
        cursor: pointer;
    }

    .user-list {
        margin-top: 10px;
        max-height: 150px;
        overflow-y: auto;
        border: 1px solid #666;
        padding: 5px;
        background-color: #2b2b2b;
        border-radius: 4px;
    }

    .user-entry {
        display: flex;
        align-items: center;
        margin-bottom: 5px;
        color: white;
    }    
</style>